/**
 * The decisions behind a dictation session (2026-09-19), pulled out of
 * VoiceInput.ts — which is exempt from coverage because it is microphone
 * plumbing — so that every choice it makes is tested.
 *
 * THE PROBLEM THIS SOLVES: audio went up in 30-second clips, so a typical
 * note had ONE clip, sent when the user pressed stop — the first moment
 * anything could fail. Someone could talk for a minute into a dead service
 * and learn it only at the end, with the audio discarded.
 *
 * Now:
 *   - the service is asked "are you ready?" the moment the mic is tapped,
 *     and a dead one sends the session to the device's own dictation;
 *   - the first clip is ~5 s, so words appear (or a problem shows) while the
 *     person is still talking;
 *   - a clip that fails is KEPT and retried — once automatically, then on a
 *     Retry button — never silently thrown away;
 *   - pressing stop never restarts listening, whatever the last clip does.
 */

import type { Feedback } from '@/lib/voice/feedback';

/** First clip: short, so success or failure shows within seconds. */
export const FIRST_CLIP_MS = 5_000;
/** Every clip after the first. */
export const CLIP_MS = 30_000;
/** How long the readiness check may take before recording starts anyway. */
export const PREFLIGHT_TIMEOUT_MS = 2_500;
/** Automatic retries per failed clip — the bound (CLAUDE.md: every retry declares one). */
export const MAX_AUTO_RETRIES = 1;
/** Wait before the automatic retry, so a blip has time to clear. */
export const RETRY_DELAY_MS = 3_000;

/**
 * How long the tidy pass waits for the last clip to land before giving up
 * (2026-09-30).
 *
 * It must not format a field that is about to gain another sentence, and it
 * must not wait forever for a clip wedged in a retry — CLAUDE.md: every retry
 * declares what bounds it. Giving up means no tidy pass, which is exactly what
 * being offline already does, so the failure is one the designer has seen.
 *
 * 20 × 250ms ≈ 5s, comfortably past one RETRY_DELAY_MS plus a round trip.
 */
export const QUIET_POLL_MS = 250;
export const QUIET_ATTEMPTS = 20;
/** Warn about a silent microphone after this long with nothing heard. */
export const SILENCE_WARN_MS = 4_000;

/** Length of clip number `index` (0-based). */
export function clipLengthMs(index: number): number {
  return index === 0 ? FIRST_CLIP_MS : CLIP_MS;
}

// ─── Before recording ───────────────────────────────────────────────────────

/** What the readiness check (GET /transcribe) found. */
export type ServiceStatus =
  | 'ready'
  /** Deployed without a provider key, or not deployed at all. */
  | 'unconfigured'
  /** Unreachable: the network or the function is down. */
  | 'down'
  /** No answer in time — don't hold the person up; the 5 s clip will tell. */
  | 'unknown';

export interface StartPlan {
  /**
   * server — record and transcribe as we go.
   * queue  — record now, transcribe when the connection is back.
   * local  — the browser's own dictation.
   * none   — nothing can work; say so.
   */
  engine: 'server' | 'queue' | 'local' | 'none';
  /**
   * What to do if 'local' can't run. Safari does speech recognition ON THE
   * DEVICE, so it works in airplane mode; Chrome sends audio to Google and
   * fails. We can't tell which we have until we try.
   */
  onLocalFailure: 'queue' | 'none';
  message: string | null;
  tone: 'info' | 'warn';
}

/**
 * Which engine a new session should use.
 *
 * NO CONNECTION → try the browser's dictation FIRST. On an iPhone that is
 * Safari's on-device recognition and it really does work offline (confirmed
 * on 2026-09-19: the words appeared in airplane mode). In Chrome the same API
 * goes to Google's servers and fails — so if it can't run, fall back to
 * recording and sending when the connection returns. Never assume either way.
 *
 * The microphone itself always works offline, which is what makes the
 * fallback possible.
 *
 * Quota isn't decided here — it's checked first, and an exhausted quota never
 * falls back to the browser's dictation (see feedback.ts: that would make the
 * free tier unlimited).
 */
export function startPlan(input: {
  online: boolean;
  service: ServiceStatus;
  localAvailable: boolean;
}): StartPlan {
  const { online, service, localAvailable } = input;
  const queueMessage = !online
    ? 'You’re offline — recording anyway. Your words will appear here once you’re back online; keep this note open until then.'
    : 'Can’t reach the transcription service — recording anyway. Your words will appear once it’s reachable; keep this note open until then.';

  if (online && (service === 'ready' || service === 'unknown')) {
    return { engine: 'server', onLocalFailure: 'none', message: 'Listening… tap the mic again to stop.', tone: 'info' };
  }

  // Offline, or the service couldn't be reached.
  if (!online || service === 'down') {
    if (!localAvailable) return { engine: 'queue', onLocalFailure: 'none', message: queueMessage, tone: 'warn' };
    return {
      engine: 'local',
      // If the browser's dictation needs the connection it hasn't got, record
      // instead — nothing is lost either way.
      onLocalFailure: 'queue',
      message: !online
        ? 'You’re offline — using your device’s dictation. Accuracy may be lower.'
        : 'Can’t reach the transcription service — using your device’s dictation. Accuracy may be lower.',
      tone: 'warn',
    };
  }

  // 'unconfigured': the connection works, our service can't. Queueing would
  // only pile up clips for a service that has nothing to transcribe with.
  if (localAvailable) {
    return {
      engine: 'local',
      onLocalFailure: 'none',
      message: 'Transcription isn’t set up here yet — using your browser’s dictation. Accuracy may be lower.',
      tone: 'warn',
    };
  }
  return {
    engine: 'none',
    onLocalFailure: 'none',
    message: 'Dictation isn’t available right now. Please type, or try again shortly.',
    tone: 'warn',
  };
}

/** What to say and do when the browser's own dictation couldn't run. */
export function afterLocalFailure(input: {
  reason: string;
  onFailure: 'queue' | 'none';
}): { engine: 'queue' | 'none'; message: string; tone: 'warn' } {
  const { reason, onFailure } = input;
  if (onFailure === 'queue') {
    return {
      engine: 'queue',
      message: reason === 'network'
        ? 'Your browser’s dictation needs a connection — recording instead. Your words will appear once you’re back online; keep this note open.'
        : 'Your browser’s dictation couldn’t run — recording instead. Your words will appear once you’re back online; keep this note open.',
      tone: 'warn',
    };
  }
  return {
    engine: 'none',
    message: reason === 'network'
      ? 'Your browser’s dictation needs an internet connection.'
      : reason === 'no-audio'
        ? 'Your browser’s dictation isn’t picking up any audio. Tap the mic to try again.'
        : 'Your browser’s dictation couldn’t start.',
    tone: 'warn',
  };
}

// ─── When a clip fails ──────────────────────────────────────────────────────

export interface ClipFailurePlan {
  /** Try this clip again after RETRY_DELAY_MS. */
  retry: boolean;
  /** Hold on to the clip for the Retry button. */
  keep: boolean;
  /** Hand the rest of this session to the device's dictation. */
  switchToLocal: boolean;
  /** End the session. */
  stop: boolean;
}

/**
 * What to do with a clip that failed, given what feedback.ts made of the
 * error, whether the person has already pressed stop, and how many times this
 * clip has been tried.
 */
export function afterClipFailure(input: {
  feedback: Pick<Feedback, 'stop' | 'fallback' | 'quota'>;
  retryable: boolean;
  userStopped: boolean;
  attempts: number;
}): ClipFailurePlan {
  const { feedback: fb, retryable, userStopped, attempts } = input;

  // Out of quota: the server will refuse this clip every time. Retrying or
  // keeping it would only promise something that can't happen.
  if (fb.quota !== undefined && fb.stop && !fb.fallback) {
    return { retry: false, keep: false, switchToLocal: false, stop: true };
  }

  const canRetry = attempts <= MAX_AUTO_RETRIES;
  const serviceProblem = fb.fallback || retryable;

  if (serviceProblem) {
    return {
      // One quick retry for a blip; after that the clip waits for Retry.
      retry: canRetry,
      keep: !canRetry,
      // Keep talking on the device's engine — but NEVER after stop: the
      // person asked the mic to stop, and restarting it was the bug.
      switchToLocal: fb.fallback && !userStopped,
      stop: false,
    };
  }

  // The clip itself was unusable (a transcription error, not an outage):
  // sending the same audio again won't help.
  if (!fb.stop) return { retry: false, keep: false, switchToLocal: false, stop: false };

  // Something unexpected. Stop the session, but keep the audio: it may well
  // be fine, and it's the person's words.
  return { retry: false, keep: true, switchToLocal: false, stop: true };
}

/** "1 clip couldn't be transcribed yet." — for the Retry prompt. */
export function keptClipsMessage(count: number, online = true): string | null {
  if (count <= 0) return null;
  const what = count === 1
    ? 'Part of your dictation hasn’t been transcribed yet — it’s saved'
    : `${count} parts of your dictation haven’t been transcribed yet — they’re saved`;
  return online
    ? `${what}.`
    : `${what} and will be sent when you’re back online. Keep this note open.`;
}

/** Asked before CLOSING a form whose dictation is still waiting to become text. */
export const VOICE_PENDING_PROMPT =
  'Some of your dictation hasn’t been transcribed yet and will be lost if you leave now.\n\nLeave anyway?';

/** Asked before SAVING one — the waiting words wouldn't be in what's saved. */
export const VOICE_PENDING_SAVE_PROMPT =
  'Some of your dictation hasn’t been transcribed yet, so it won’t be included if you save now.\n\nSave anyway?';

// ─── While recording ────────────────────────────────────────────────────────

/**
 * Loudness 0–1 from an AnalyserNode's time-domain bytes (128 = silence).
 * Root-mean-square, scaled so ordinary speech reads around the middle.
 */
export function levelFromSamples(samples: ArrayLike<number>): number {
  if (samples.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < samples.length; i++) {
    const v = (samples[i]! - 128) / 128;
    sum += v * v;
  }
  const rms = Math.sqrt(sum / samples.length);
  return Math.min(1, rms * 4);
}

/** How many of five bars to light. Anything below a whisper lights none. */
export function barsLit(level: number, bars = 5): number {
  if (level < 0.04) return 0;
  return Math.min(bars, Math.max(1, Math.ceil(level * bars)));
}

/** True when nothing has been heard for long enough to warn about. */
export function isSilentTooLong(lastHeardAt: number, now: number): boolean {
  return now - lastHeardAt >= SILENCE_WARN_MS;
}

/** "0:07", "1:32". */
export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * A file name whose extension matches the audio inside. The transcriber goes
 * by the extension, and iPhones record MP4 audio — which went up named
 * "clip.webm" until 2026-09-19.
 */
export function clipFileName(mimeType: string | null | undefined): string {
  const t = (mimeType ?? '').toLowerCase();
  if (t.includes('mp4') || t.includes('aac') || t.includes('m4a')) return 'clip.mp4';
  if (t.includes('ogg')) return 'clip.ogg';
  if (t.includes('wav')) return 'clip.wav';
  if (t.includes('mpeg') || t.includes('mp3')) return 'clip.mp3';
  return 'clip.webm';
}
